import * as React from 'react'
import { fetchDeployedVersion, isNewerDeploy } from '@/helpers/appVersion'

/** How often to re-check `/version.json` while the tab is open. */
const DEPLOY_REFRESH_POLL_MS = 60_000

/**
 * Captures the frontend SHA on first successful `/version.json` read, then
 * polls (interval + tab focus) and returns true once a different SHA is live.
 * Local HMR does not change that file, so Vite refreshes do not trip the bar.
 */
export function useDeployRefresh(): boolean {
  const [updateAvailable, setUpdateAvailable] = React.useState(false)
  const runningShaRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (updateAvailable) return

    let cancelled = false

    async function check(): Promise<void> {
      const deployed = await fetchDeployedVersion()
      if (cancelled || deployed == null) return
      if (runningShaRef.current == null) {
        runningShaRef.current = deployed.sha
        return
      }
      if (isNewerDeploy(runningShaRef.current, deployed.sha)) {
        setUpdateAvailable(true)
      }
    }

    void check()
    const intervalId = window.setInterval(() => {
      void check()
    }, DEPLOY_REFRESH_POLL_MS)

    function onFocus(): void {
      void check()
    }

    function onVisibility(): void {
      if (document.visibilityState === 'visible') void check()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [updateAvailable])

  return updateAvailable
}
